<template>
    <BaseForm>
        <template #fields>
            <MultiLinkGroup>
                <MultiLink
                    :label="$t('competition-national-label')"
                    :help-button-text="$t('competition-national-help')"
                    :links="values.competitionNational"
                    :error-messages="errors.competitionNational"
                    @input="setFieldValue('competitionNational', $event)"
                    @delete="setFieldValue('competitionNational', $event)"
                />
                <MultiLink
                    :multilink-id="otherCompetitionNationalId"
                    :label="$t('other-competition-national')"
                    :help-button-text="$t('other-competition-national-help')"
                    :links="values.otherCompetitionNational"
                    :error-messages="errors.otherCompetitionNational"
                    @input="setFieldValue('otherCompetitionNational', $event)"
                    @delete="setFieldValue('otherCompetitionNational', $event)"
                />
                <MultiLink
                    :multilink-id="competitionInternationalId"
                    :label="$t('competition-international-label')"
                    :help-button-text="$t('competition-international-help')"
                    :links="values.competitionInternational"
                    :error-messages="errors.competitionInternational"
                    @input="setFieldValue('competitionInternational', $event)"
                    @delete="setFieldValue('competitionInternational', $event)"
                />
                <MultiLink
                    :multilink-id="otherCompetitionInternationalId"
                    :label="$t('other-competition-international')"
                    :help-button-text="$t('other-competition-international-help')"
                    :links="values.otherCompetitionInternational"
                    :error-messages="errors.otherCompetitionInternational"
                    @input="setFieldValue('otherCompetitionInternational', $event)"
                    @delete="setFieldValue('otherCompetitionInternational', $event)"
                />
            </MultiLinkGroup>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import MultiLinkGroup from '@/components/MultiLink/MultiLinkGroup.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesCompetitionSchema } from '../schemas';

defineProps<{ seriesId: string }>();

const { series, createEventSeriesMultiLink } = useEventSeriesInjection();
const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createEventSeriesMultiLink);

const competitionNationalId = computed(() => series.value?.competitionNational?.id);
const otherCompetitionNationalId = computed(() => series.value?.otherCompetitionNational?.id);
const competitionInternationalId = computed(() => series.value?.competitionInternational?.id);
const otherCompetitionInternationalId = computed(() => series.value?.otherCompetitionInternational?.id);

const competitionNationalLinks = computed(() => series.value?.competitionNational?.links.nodes ?? []);
const otherCompetitionNationalLinks = computed(() => series.value?.otherCompetitionNational?.links.nodes ?? []);
const competitionInternationalLinks = computed(() => series.value?.competitionInternational?.links.nodes ?? []);
const otherCompetitionInternationalLinks = computed(
    () => series.value?.otherCompetitionInternational?.links.nodes ?? [],
);

const { errors, values, setFieldValue, validate, onDiscard, resetForm } = useForm(
    EventSeriesCompetitionSchema,
    computed(() => ({
        competitionNational: getFormFieldLinks(competitionNationalLinks),
        otherCompetitionNational: getFormFieldLinks(otherCompetitionNationalLinks),
        competitionInternational: getFormFieldLinks(competitionInternationalLinks),
        otherCompetitionInternational: getFormFieldLinks(otherCompetitionInternationalLinks),
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await saveMultiLinkFields([
            {
                id: competitionNationalId.value,
                fieldName: 'competitionNational',
                links: values.value.competitionNational,
            },
            {
                id: otherCompetitionNationalId.value,
                fieldName: 'otherCompetitionNational',
                links: values.value.otherCompetitionNational,
            },
            {
                id: competitionInternationalId.value,
                fieldName: 'competitionInternational',
                links: values.value.competitionInternational,
            },
            {
                id: otherCompetitionInternationalId.value,
                fieldName: 'otherCompetitionInternational',
                links: values.value.otherCompetitionInternational,
            },
        ]);
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
